import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getUserPermissions,
  isAdmin,
  isSuperAdmin,
  canManageContent,
  canViewContent,
  canManageAnnouncements,
  canViewAnnouncements,
  canManageHolidays,
  canViewHolidays,
  canViewPolicies,
  canManageEmployees,
  canViewEmployees,
  canEditEmployees,
  canAssignRoles,
  canAddAdmin,
  canSeeEmployeeDirectory,
  canSeeProjectManagement,
  canManageProjects,
  canViewProjectRates,
  canAccessAllInvoices,
  MODULES,
  PERMISSIONS
} from '../utils/permissions';
/**
 * Custom hook for role-based permissions
 * Provides easy access to permission checking functions
 */
export const usePermissions = () => {
  const { user } = useAuth();
  // Get user roles from the user object
  const userRoles = useMemo(() => {
    if (!user || !user.roles) {
      return [];
    }
    // Handle both array and string formats
    if (Array.isArray(user.roles)) {
      return user.roles;
    }
    // If roles is a string, split by comma or return as single item
    if (typeof user.roles === 'string') {
      return user.roles.split(',').map(role => role.trim());
    }
    return [];
  }, [user]);
  // Memoized permission functions
  const permissions = useMemo(() => ({
    // General permission functions
    hasPermission: (module, permission) => hasPermission(userRoles, module, permission),
    hasAnyPermission: (module, permissions) => hasAnyPermission(userRoles, module, permissions),
    hasAllPermissions: (module, permissions) => hasAllPermissions(userRoles, module, permissions),
    getUserPermissions: (module) => getUserPermissions(userRoles, module),
    // Role checking functions
    isAdmin: () => isAdmin(userRoles),
    isSuperAdmin: () => isSuperAdmin(userRoles),
    // Content management functions
    canManageContent: (module) => canManageContent(userRoles, module),
    canViewContent: (module) => canViewContent(userRoles, module),
    // Specific module functions
    canManageAnnouncements: () => canManageAnnouncements(userRoles),
    canViewAnnouncements: () => canViewAnnouncements(userRoles),
    canManageHolidays: () => canManageHolidays(userRoles),
    canViewHolidays: () => canViewHolidays(userRoles),
    canViewPolicies: () => canViewPolicies(userRoles),
    canManageEmployees: () => canManageEmployees(userRoles),
    canViewEmployees: () => canViewEmployees(userRoles),
    canEditEmployees: () => canEditEmployees(userRoles),
    canAssignRoles: () => canAssignRoles(userRoles),
    canAddAdmin: () => canAddAdmin(userRoles),
    canSeeEmployeeDirectory: () => canSeeEmployeeDirectory(userRoles),
    canSeeProjectManagement: () => canSeeProjectManagement(userRoles),
    canManageProjects: () => canManageProjects(userRoles),
    canViewProjectRates: () => canViewProjectRates(userRoles),
    canAccessAllInvoices: () => canAccessAllInvoices(userRoles),
    // User roles for debugging or custom logic
    userRoles
  }), [userRoles]);
  return permissions;
};
// Export constants for easy access
export { MODULES, PERMISSIONS }; 